import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import { withStyles } from '@material-ui/core/styles'
import createStyles from '@material-ui/core/styles/createStyles'
import { Theme } from '@material-ui/core/styles/createTheme'
import MapBoxGL, { Marker, NavigationControl, ScaleControl } from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAgencySettings } from 'src/layout/AgencySettings'
import { useViewport } from 'src/layout/hooks'
import SETTINGS from 'src/settings'

import { ComponentControl } from '../hooks/useComponentControl'

import { IMapProps, MapContext } from './index'

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      borderRadius: 'inherit',
    },
    map: {
      borderRadius: 'inherit',
    },
    button: {
      width: '100%',
      margin: `0px ${theme.spacing()}px`,
    },
  })

const defaultOpts: MapBoxGL.MapboxOptions = {
  minZoom: 0,
  maxZoom: 20,
  hash: false,
  container: '',
  preserveDrawingBuffer: false,
  scrollZoom: true,
  interactive: true,
  dragRotate: true,
  attributionControl: true,
  logoPosition: 'bottom-left',
  renderWorldCopies: true,
  trackResize: true,
  touchZoomRotate: true,
  doubleClickZoom: true,
  keyboard: true,
  dragPan: true,
  boxZoom: true,
  refreshExpiredTiles: true,
  failIfMajorPerformanceCaveat: false,
  bearingSnap: 7,
  style: 'mapbox://styles/mapbox/light-v9',
}

const useMap = (
  token: string,
  opts: MapBoxGL.MapboxOptions
): { initMap: (c: HTMLElement, o: any) => void; map: MapBoxGL.Map | undefined; ready: boolean } => {
  const [map, setMap] = useState<MapBoxGL.Map | undefined>(undefined)

  const [mapOptions] = useState<MapBoxGL.MapboxOptions>({
    ...defaultOpts,
    ...opts,
  })
  const [ready, setReady] = useState<boolean>(false)

  useEffect(() => {
    if (token) {
      MapBoxGL.accessToken = token
    }
    return () => {
      if (map) {
        map.remove()
        setMap(undefined)
      }
    }
  }, [])

  const initMap = (container: HTMLElement, options: any) => {
    const newMap = new MapBoxGL.Map({ ...mapOptions, container, ...options })

    // This seems to be needed in mapbox v1 for the MapboxGeocoder's marker to work
    // otherwise, it fails with "Uncaught TypeError: this._mapboxgl.Marker is not a constructor"
    Object.assign(newMap, { Marker: Marker })

    // add zoom controls
    newMap.addControl(new NavigationControl({ showZoom: true, showCompass: false }), 'bottom-right')

    newMap.on('load', () => {
      setReady(true)
      setMap(newMap)
    })
  }

  return { map, initMap, ready }
}

const MapBoxMap = (props: IMapProps) => {
  const agencyViewport = useViewport()
  const {
    classes,
    children,
    height,
    width,
    viewport = agencyViewport,
    showSatellite = false,
    showScale = false,
    showSearchBar = false,
    onMapReady,
    componentControl,
    options = {},
  } = props
  const { t } = useTranslation()

  const { map, initMap, ready } = useMap(SETTINGS.app.mapbox_token, options)
  const { distance_unit } = useAgencySettings()
  const container = useRef<HTMLDivElement | null>(null)
  const [oldComponentControl, setOldComponentControl] = useState<ComponentControl | undefined>(undefined)

  const loadBaseLayers = (): void => {
    if (!map) {
      return
    }

    map.addSource('mapbox-satellite', {
      type: 'raster',
      tiles: [
        `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v10/tiles/{z}/{x}/{y}?access_token=${SETTINGS.app.mapbox_token}`,
      ],
      tileSize: 256,
      // ..
    })
    map.addLayer({
      type: 'raster',
      id: 'satellite-map',
      source: 'mapbox-satellite',
      // ..
    })
    map.setLayoutProperty('satellite-map', 'visibility', showSatellite ? 'visible' : 'none')
    if (showSearchBar) {
      const geocoder = new MapboxGeocoder({
        accessToken: SETTINGS.app.mapbox_token,
        mapboxgl: map,
        marker: true,
      })
      map.addControl(geocoder, showSearchBar)
    }

    if (showScale) {
      const unit = distance_unit === 'miles' ? 'imperial' : 'metric'
      map.addControl(new ScaleControl({ unit: unit }), showScale)
    }
  }

  // Init map while container is available
  useEffect(() => {
    if (container) {
      initMap(container.current!, {
        ...viewport,
      })
    }
  }, [container])

  useEffect(() => {
    if (ready && map) {
      if (onMapReady) {
        loadBaseLayers()
        onMapReady(map)

        const { layers = [] } = (map && map.getStyle()) || { layers: [] }
        layers.forEach((layer: any) => {
          if (layer.layout && layer.layout['text-field']) {
            map.setLayoutProperty(layer.id, 'text-field', ['get', t('language.map')])
          }
        })
        if (componentControl) {
          map.addControl(componentControl, 'bottom-right')
          setOldComponentControl(componentControl)
        }
      }
    }
  }, [map, ready])

  /** On container resize */
  useEffect(() => {
    if (ready && map) {
      map.resize()
    }
  }, [props.width, props.height, ready, map])

  /** On satellite mode change */
  useEffect(() => {
    if (map) {
      if (showSatellite) {
        map.setLayoutProperty('satellite-map', 'visibility', 'visible')
      } else {
        map.setLayoutProperty('satellite-map', 'visibility', 'none')
      }
    }
  }, [showSatellite])

  useEffect(() => {
    if (map && componentControl) {
      if (oldComponentControl) {
        map.removeControl(oldComponentControl)
      }
      map.addControl(componentControl, 'bottom-right')
      setOldComponentControl(componentControl)
    }
  }, [componentControl])

  return (
    <MapContext.Provider value={map}>
      <div style={{ height, width }} ref={container} className={classes.map}>
        {children}
      </div>
    </MapContext.Provider>
  )
}

export default withStyles(styles)(MapBoxMap)
